import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Box, ButtonBase } from '@material-ui/core';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import LocalizationSection from './LocalizationSection';
import MobileSection from './MobileSection';
import ProfileSection from './ProfileSection';
import NotificationSection from './NotificationSection';
import DialerResetButton from './DialerResetButton';
import CohortStartDialer from './CohortStartDialer';
import PredictiveCohortDialer from './PredictiveCohortDialer';
import CallHistoryButton from './CallHistoryButton';
import PredictiveSessionButton from './PredictiveSessionButton';
import DeliveryCenterHubTile from './DeliveryCenterHubTile';
import ProductCategoryButton from './ProductCategoryButton';

// assets
import { IconMenu2 } from '@tabler/icons';
import DialerHeader from '../../../menu-items/Dashboard/AutoDailer/DialerInitialState';
import DialerTimer from '../../../menu-items/Dashboard/AutoDailer/DialerTimer';
import DialerMode from '../../../menu-items/Dashboard/AutoDailer/DialerMode';
import DialerInitialState from '../../../menu-items/Dashboard/AutoDailer/DialerInitialState';
import DialerBreakState from '../../../menu-items/Dashboard/AutoDailer/DialerBreakState';
import TicketInfo from '../../../menu-items/Dashboard/AutoDailer/TicketInfo';
import { getDecryptDataFromLocal } from '../../../utils/encrypt';
import DashboardManager from '../../../menu-items/MainDashboard/Default/DashboardManager';
import { useDashboard } from '../../../context/DashboardContext';

// style constant
const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1,
        textAlign: 'center'
    },
    headerAvatar: {
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        transition: 'all .2s ease-in-out',
        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
        color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
        '&:hover': {
            background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
            color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
        }
    },
    boxContainer: {
        width: '228px',
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            width: 'auto'
        }
    }
}));

//-----------------------|| MAIN NAVBAR / HEADER ||-----------------------//

const Header = ({ handleLeftDrawerToggle }) => {
    const classes = useStyles();
    const dashboard = useDashboard();

    let role = getDecryptDataFromLocal('setRole')


    return (
        <React.Fragment>
            {/* logo & toggler button */}
            <div className={classes.boxContainer}>
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>
                <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Avatar variant="rounded" className={classes.headerAvatar} onClick={handleLeftDrawerToggle} color="inherit">
                        <IconMenu2 stroke={1.5} size="1.3rem" />
                    </Avatar>
                </ButtonBase>
            </div>

            {/* dashboard manager filters */}
            <Box sx={{ ml: 3, flexShrink: 0 }}>
                {(role == 1 || role == 2 || role == 14 || role == 17) && dashboard ? (
                    <DashboardManager
                        agentType={dashboard.agentType}
                        setAgentType={dashboard.setAgentType}
                        handleViewManagerWise={dashboard.handleViewManagerWise}
                        compact
                    />
                ) : null}
            </Box>

            {/* "At Delivery Center" hub tile — RTO-risk heads-up, see
                DeliveryCenterHubTile.js. Self-gates to roles 1/2/3/4/5/13/14/17.
                Wrapped in a growing, centered box so the tile sits centered in
                the space between the filters and the right-side icon cluster,
                without stealing the spacer's job of pushing profile right. */}
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                <DeliveryCenterHubTile />
            </Box>

            {/* spacer pushes profile to far right */}
            <div className={classes.grow} />

            {/* Start Dialler moved to the My Chats screen (the dialer cockpit) —
                the agent needs the buyer's chat as context + a wrap-up form on
                each call, so it can't be a global header button. See
                menu-items/Dashboard/MyChats.js + DialerCallPane.js. 2026-08-25 */}

            {/* Dialer reset — frees a softphone line Phonomenal has left stuck
                on a finished call (agent otherwise cannot dial at all). Only for
                people who actually use the dialer: agents (5) and TLs (13).
                Shailendra 2026-08-24. See DialerResetButton.js */}
            {(Number(role) === 5 || Number(role) === 13) && <DialerResetButton />}

            {/* Cohort (repeat) agents have NO My Chats, so their Start Dialler
                lives in the top bar. Self-gates via /voice/dialer/is-cohort — it
                renders null for everyone else, incl new-rev agents (who start from
                My Chats). Shailendra 2026-08-27. */}
            {(Number(role) === 5 || Number(role) === 13) && <CohortStartDialer />}

            {/* Cohort agents on the predictive engine (2026-09-16) — same
                floating cockpit as CohortStartDialer above, fed from Phonomenal's
                predictive status instead of our own queue. Self-gates to
                cohortTeam + predictiveDialer via its own status poll. */}
            {(Number(role) === 5 || Number(role) === 13) && <PredictiveCohortDialer />}

            {/* Call History — global so agents with no "My Chats" menu item
                (e.g. cohort/repeat agents) can still reach it. 2026-08-31. */}
            <CallHistoryButton />

            {/* Predictive dialer Go Online/Offline — self-gates to agents with
                admins.predictiveDialer:true (New_NC_1 rollout, 2026-09-14).
                Always in the top bar (not My Chats) since a predictive agent
                needs to be reachable regardless of which page they're on. */}
            <PredictiveSessionButton />

            {/* notification & profile */}
            <NotificationSection />
            {/* Product margin categories (A/B/C) — self-gates to roles
                2/3/4/5/13/14/17, not 1. See ProductCategoryButton.js. */}
            <ProductCategoryButton />
            <ProfileSection />

            {/* mobile header */}
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <MobileSection />
            </Box>
        </React.Fragment>
    );
};

Header.propTypes = {
    handleLeftDrawerToggle: PropTypes.func
};

export default Header;
